import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  PDFViewer,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import logo from "../../components/Images/para.png";

// ─── Helpers ─────────────────────────────────────────────────────
const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

const formatCurrency = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return "0.00";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// ─── Styles ──────────────────────────────────────────────────────
const styles = StyleSheet.create({
  page: {
    padding: 25,
    fontSize: 9,
    fontFamily: "Helvetica",
    color: "#000",
  },

  billReceiptTop: {
    width: "100%",
    textAlign: "center",
    marginBottom: 4,
  },
  billReceiptText: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 1,
    textAlign: "center",
  },

  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 2,
  },

  leftBox: {
    width: 260,
    alignItems: "flex-end",
  },
  jurisdiction: {
    fontSize: 8,
    color: "#333",
    marginBottom: 2,
  },
  companyName: {
    fontSize: 15,
    color: "#a12626",
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  companySub: {
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  addressText: {
    fontSize: 8,
    color: "#111",
    marginBottom: 1,
    textAlign: "left",
  },

  logoBox: {
    width: 70,
    height: 70,
    justifyContent: "flex-start",
    alignItems: "flex-end",
  },
  logo: {
    width: 60,
    height: 60,
    objectFit: "contain",
  },

  divider: {
    borderBottom: "1pt solid #000",
    marginTop: 2,
    marginBottom: 6,
  },

  gstinBox: {
    marginBottom: 2,
    alignItems: "flex-start",
  },
  gstinText: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
    textAlign: "left",
  },

  // ─── GSTIN + Summary same row ──────────────
  gstinSummaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 2,
  },
  summaryItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  summaryLabel: {
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    color: "#333",
  },
  summaryValue: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#000",
    marginLeft: 3,
  },
  summarySeparator: {
    fontSize: 9,
    color: "#999",
    marginHorizontal: 6,
  },
  summaryRight: {
    flexDirection: "row",
    alignItems: "center",
  },

  // ─── Table Title Header ──────────────────
  tableTitle: {
    backgroundColor: "#E5E7EB",
    padding: 4,
    marginBottom: 0,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: "#000",
  },
  tableTitleText: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    textAlign: "left",
    color: "#111",
  },

  // ─── Tables ──────────────────────────────
  table: {
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    marginBottom: 12,
  },
  row: {
    flexDirection: "row",
  },
  headerCell: {
    borderRightWidth: 1,
    borderColor: "#000",
    padding: 4,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    textAlign: "center",
    backgroundColor: "#F3F4F6",
  },
  cell: {
    borderRightWidth: 1,
    borderTopWidth: 1,
    borderColor: "#000",
    padding: 2,
    fontSize: 9,
    textAlign: "center",
  },
  lastCol: {
    borderRightWidth: 0,
  },

  // ─── Total Row ──────────────────────────
  totalRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderColor: "#000",
  },
  totalLabelCell: {
    borderColor: "#000",
    padding: 4,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    textAlign: "left",
    backgroundColor: "#F3F4F6",
  },
  totalValueCell: {
    borderRightWidth: 1,
    borderColor: "#000",
    padding: 6,
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    textAlign: "center",
    backgroundColor: "#F3F4F6",
  },

  footer: {
    position: "absolute",
    bottom: 15,
    left: 25,
    right: 25,
    textAlign: "center",
    fontSize: 7.5,
    color: "#666",
  },
});

// ─── LR Table ──────────────────────────────────────────────
const FreightTable = ({ freights = [] }) => {
  const cols = [
    { key: "lr_number", label: "LR No", width: "13%" },
    { key: "truck_no", label: "Truck No", width: "20%" },
    { key: "loading_wt", label: "Load Wt", width: "14%" },
    { key: "received_wt", label: "Unload Wt", width: "14%" },
    { key: "net_payable", label: "Net Payable", width: "18%" },
    { key: "created_at", label: "Date", width: "21%" },
  ];

  return (
    <View>
      <View style={styles.tableTitle}>
        <Text style={styles.tableTitleText}>Payment Against LR</Text>
      </View>

      <View style={styles.table}>
        <View style={styles.row}>
          {cols.map((c, i) => (
            <View
              key={c.key}
              style={[
                styles.headerCell,
                { width: c.width },
                i === cols.length - 1 && styles.lastCol,
              ]}
            >
              <Text>{c.label}</Text>
            </View>
          ))}
        </View>

        {freights.map((f, idx) => (
          <View style={styles.row} key={idx}>
            {cols.map((c, i) => {
              let value = "";
              if (c.key === "created_at") {
                value = formatDate(f.created_at);
              } else if (c.key === "lr_number") {
                value = f.lr_number || `#${f.freight_id || ""}`;
              } else if (c.key === "net_payable") {
                const raw = f[c.key];
                value =
                  raw !== null && raw !== undefined && raw !== ""
                    ? formatCurrency(raw)
                    : "";
              } else {
                const raw = f[c.key];
                value =
                  raw !== null && raw !== undefined && raw !== ""
                    ? String(raw)
                    : "";
              }
              return (
                <View
                  key={c.key}
                  style={[
                    styles.cell,
                    { width: c.width },
                    i === cols.length - 1 && styles.lastCol,
                  ]}
                >
                  <Text>{value}</Text>
                </View>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
};

// ─── Payment Table ──────────────────────────────────────────────
const PaymentTable = ({ payments = [], createdAt }) => {
  const cols = [
    { key: "pay_amount", label: "Paid Amt", width: "14%" },
    { key: "pay_mode", label: "Pay Mode", width: "14%" },
    { key: "upi_check_no", label: "UPI/Cheque No.", width: "19%" },
    { key: "payment_to", label: "Payment To", width: "17%" },
    { key: "remark", label: "Remark", width: "20%" },
    { key: "pay_date", label: "Pay Date", width: "16%" },
  ];

  const totalPaid = payments.reduce((sum, p) => {
    const amt = parseFloat(p.pay_amount);
    return sum + (isNaN(amt) ? 0 : amt);
  }, 0);

  const paidAmtWidth = "14%";   // first column — shows total value
  const restWidth = "86%";      // remaining columns merged — shows "Total Paid" label

  return (
    <View>
      <View style={styles.tableTitle}>
        <Text style={styles.tableTitleText}>Transaction Details</Text>
      </View>

      <View style={styles.table}>
        {/* Header Row */}
        <View style={styles.row}>
          {cols.map((c, i) => (
            <View
              key={c.key}
              style={[
                styles.headerCell,
                { width: c.width },
                i === cols.length - 1 && styles.lastCol,
              ]}
            >
              <Text>{c.label}</Text>
            </View>
          ))}
        </View>

        {/* Data Rows */}
        {payments.map((p, idx) => (
          <View style={styles.row} key={idx}>
            {cols.map((c, i) => {
              let value = "";
              if (c.key === "pay_date") {
                value = formatDate(p.pay_date || createdAt);
              } else if (c.key === "pay_amount") {
                const raw = p[c.key];
                value =
                  raw !== null && raw !== undefined && raw !== ""
                    ? formatCurrency(raw)
                    : "";
              } else {
                const raw = p[c.key];
                value =
                  raw !== null && raw !== undefined && raw !== ""
                    ? String(raw)
                    : "";
              }
              return (
                <View
                  key={c.key}
                  style={[
                    styles.cell,
                    { width: c.width },
                    i === cols.length - 1 && styles.lastCol,
                  ]}
                >
                  <Text>{value}</Text>
                </View>
              );
            })}
          </View>
        ))}

        {/* ✅ Total Row — Amount FIRST, then "Total Paid" label */}
        <View style={styles.totalRow}>
          {/* First: Paid Amt column with total value */}
          <View
            style={[
              styles.totalValueCell,
              {
                width: paidAmtWidth,
              },
            ]}
          >
            <Text>{formatCurrency(totalPaid)}</Text>
          </View>
          {/* Rest: merged columns with "Total Paid" label */}
          <View
            style={[
              styles.totalLabelCell,
              {
                width: restWidth,
                borderRightWidth: 0,
              },
            ]}
          >
            <Text>Total Paid</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

// ─── Main PDF Document ─────────────────────────────────────────
const PaymentInvoiceDocument = ({ data = {} }) => {
  const freights = Array.isArray(data?.freights) ? data.freights : [];
  const payments = Array.isArray(data?.payments) ? data.payments : [];

  const totalAmount = parseFloat(data.total_amount) || 0;
  const paidAmount = parseFloat(data.paid_amount) || 0;
  const remAmount = parseFloat(data.rem_amount) || 0;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* BILL RECEIPT */}
        <View style={styles.billReceiptTop}>
          <Text style={styles.billReceiptText}>INVOICE DETAILS</Text>
        </View>

        {/* Logo LEFT + Company info RIGHT */}
        <View style={styles.headerRow}>
          <View style={styles.logoBox}>
            <Image src={logo} style={styles.logo} />
          </View>
          <View style={styles.leftBox}>
            <Text style={styles.jurisdiction}>
              Subject to BHILAI jurisdiction
            </Text>
            <Text style={styles.companyName}>PARAS ROADLINES</Text>
            <Text style={styles.companySub}>
              (FLEET OWNER & TRANSPORTING CONTRACTORS)
            </Text>
            <Text style={styles.addressText}>
              52, R.S.S. Market, Power House, Bhilai - 490011 (C.G.)
            </Text>
            <Text style={styles.addressText}>
              MOB : 7974125386, 7999552398, 9425234581
            </Text>
          </View>
        </View>

        {/* GSTIN */}
        <View style={styles.gstinBox}>
          <Text style={styles.gstinText}>GSTIN NO. : 27AAICV3639B12C</Text>
        </View>

        {/* ✅ DAILY SERVICE + Summary — SAME ROW */}
        <View style={styles.gstinSummaryRow}>
          {/* Left: DAILY SERVICE */}
          <Text style={styles.gstinText}>DAILY SERVICE : ALL OVER INDIA</Text>

          {/* Right: Summary inline */}
          <View style={styles.summaryRight}>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Total Amt:</Text>
              <Text style={styles.summaryValue}>
                {formatCurrency(totalAmount)}
              </Text>
            </View>

            <Text style={styles.summarySeparator}>|</Text>

            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Paid:</Text>
              <Text style={styles.summaryValue}>
                {formatCurrency(paidAmount)}
              </Text>
            </View>

            <Text style={styles.summarySeparator}>|</Text>

            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Remaining:</Text>
              <Text
                style={[
                  styles.summaryValue,
                  { color: remAmount > 0 ? "#DC2626" : "#16A34A" },
                ]}
              >
                {formatCurrency(remAmount)}
              </Text>
            </View>
          </View>
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* LR Table */}
        <FreightTable freights={freights} />

        {/* Payment Table */}
        <PaymentTable payments={payments} createdAt={data?.created_at} />

        <Text style={styles.footer}>
          Powered by: YASHVITECH IT SOLUTION PVT LTD
        </Text>
      </Page>
    </Document>
  );
};

// ─── Wrapper Component ─────────────────────────────────────────
export default function PaymentInvoicePDF({ data, mode = "preview" }) {
  if (mode === "download") {
    return (
      <PDFDownloadLink
        key={data?.freight_payment_id + Date.now()}
        document={<PaymentInvoiceDocument data={data} />}
        fileName={`Payment_${data?.freight_payment_id || "invoice"}.pdf`}
        style={{
          padding: "6px 12px",
          background: "#4f46e5",
          color: "#fff",
          borderRadius: 4,
          textDecoration: "none",
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {({ loading }) => (loading ? "Preparing PDF..." : "Download PDF")}
      </PDFDownloadLink>
    );
  }

  return (
    <PDFViewer
      key={data?.freight_payment_id + "_" + Date.now()}
      style={{ width: "100%", height: "90vh", border: "none" }}
    >
      <PaymentInvoiceDocument data={data} />
    </PDFViewer>
  );
}

export { PaymentInvoiceDocument };